import { ReactNode, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/core/auth/auth";
import {
  LayoutDashboard,
  Users,
  Flag,
  FileText,
  ClipboardList,
  ArrowLeft,
  Shield,
  BarChart3,
  AlertTriangle,
  Activity,
  PanelLeftClose,
  PanelLeft,
  ListChecks,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useEffect } from "react";
import { toast } from "sonner";

interface AdminLayoutProps {
  children: ReactNode;
  title: string;
  description?: string;
}

// Navigation grouped by category
const navSections = [
  {
    title: "Operations",
    items: [
      { href: "/admin", label: "Dashboard", icon: LayoutDashboard },
      { href: "/admin/report-runs", label: "Report Runs", icon: Activity },
      { href: "/admin/errors", label: "Errors", icon: AlertTriangle },
    ],
  },
  {
    title: "Clinical",
    items: [
      { href: "/admin/templates", label: "Templates", icon: FileText },
    ],
  },
  {
    title: "Analytics",
    items: [
      { href: "/admin/analytics", label: "Overview", icon: BarChart3 },
      { href: "/admin/analytics/users", label: "User Activity", icon: Users },
    ],
  },
  {
    title: "Platform",
    items: [
      { href: "/admin/users", label: "Users", icon: Users },
      { href: "/admin/feature-flags", label: "Feature Flags", icon: Flag },
      { href: "/admin/audit-log", label: "Audit Log", icon: ClipboardList },
    ],
  },
];

// Flat list for backward compatibility
const navItems = navSections.flatMap((section) => section.items);

export function AdminLayout({ children, title, description }: AdminLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAdmin, loading, user } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    if (!loading && !isAdmin) {
      toast.error("Access denied: Admin privileges required");
      navigate("/app/livespeak");
    }
  }, [isAdmin, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <TooltipProvider delayDuration={0}>
      <div className="min-h-screen bg-background overflow-x-hidden">
        {/* Header */}
        <header className="border-b bg-card">
          <div className="flex items-center justify-between px-4 sm:px-6 py-4">
            <div className="flex items-center gap-2 sm:gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/app/livespeak")}
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="hidden sm:inline">Back to App</span>
              </Button>
              <div className="h-6 w-px bg-border hidden sm:block" />
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                <span className="font-semibold hidden sm:inline">Admin Portal</span>
              </div>
            </div>
            <div className="text-sm text-muted-foreground hidden md:block">
              Logged in as {user?.email}
            </div>
          </div>
        </header>

        <div className="flex">
          {/* Sidebar */}
          <aside
            className={cn(
              "border-r bg-card min-h-[calc(100vh-73px)] transition-all duration-300 flex-shrink-0",
              sidebarCollapsed ? "w-16" : "w-64"
            )}
          >
            {/* Collapse Toggle */}
            <div className="p-2 border-b">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className={cn(
                  "w-full justify-center",
                  !sidebarCollapsed && "justify-end"
                )}
              >
                {sidebarCollapsed ? (
                  <PanelLeft className="h-4 w-4" />
                ) : (
                  <PanelLeftClose className="h-4 w-4" />
                )}
              </Button>
            </div>

            <nav className={cn("p-2 space-y-4", !sidebarCollapsed && "p-4 space-y-6")}>
              {navSections.map((section) => (
                <div key={section.title}>
                  {!sidebarCollapsed && (
                    <h3 className="px-3 mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      {section.title}
                    </h3>
                  )}
                  <div className="space-y-1">
                    {section.items.map((item) => {
                      const isActive = location.pathname === item.href;
                      const linkContent = (
                        <Link
                          key={item.href}
                          to={item.href}
                          className={cn(
                            "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                            isActive
                              ? "bg-primary text-primary-foreground"
                              : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                            sidebarCollapsed && "justify-center px-2"
                          )}
                        >
                          <item.icon className="h-4 w-4 flex-shrink-0" />
                          {!sidebarCollapsed && item.label}
                        </Link>
                      );

                      if (sidebarCollapsed) {
                        return (
                          <Tooltip key={item.href}>
                            <TooltipTrigger asChild>
                              {linkContent}
                            </TooltipTrigger>
                            <TooltipContent side="right">
                              {item.label}
                            </TooltipContent>
                          </Tooltip>
                        );
                      }

                      return linkContent;
                    })}
                  </div>
                </div>
              ))}
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1 p-4 sm:p-6 min-w-0 overflow-x-hidden">
            <div className="mb-6">
              <h1 className="text-xl sm:text-2xl font-bold">{title}</h1>
              {description && (
                <p className="text-muted-foreground mt-1 text-sm sm:text-base">{description}</p>
              )}
            </div>
            {children}
          </main>
        </div>
      </div>
    </TooltipProvider>
  );
}
